import React, { useState, useEffect } from 'react';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  styled,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MainCategory = () => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal,setShowUserGuideModal]=useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddMainCategoryModal, setShowAddMainCategoryModal] = useState(false);
  const [showEditMainCategoryModal, setShowEditMainCategoryModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mainCategory, setMainCategory] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState({ mainCategoryId: null, mainCategory: '', description: '' });

  // Static MainCategory data
  const staticMainCategoryData = [
    { mainCategoryId: 1, mainCategory: 'Electronics', description: 'Electronic items and gadgets' },
    { mainCategoryId: 2, mainCategory: 'Clothing', description: 'Clothing and apparel' },
    { mainCategoryId: 3, mainCategory: 'Books', description: 'Books and literature' },
    { mainCategoryId: 4, mainCategory: 'Home & Kitchen', description: 'Home and kitchen appliances' },
    { mainCategoryId: 5, mainCategory: 'Sports', description: 'Sports and outdoor equipment' },
    { mainCategoryId: 6, mainCategory: 'Toys', description: 'Toys and games' },
  ];

  const [mainCategories, setMainCategories] = useState(staticMainCategoryData);
  const [filteredData, setFilteredData] = useState(staticMainCategoryData);

  const handleAddMainCategory = () => {
    setMainCategory('');
    setDescription('');
    setShowAddMainCategoryModal(true);
  };

  const handleEdit = (category) => {
    setSelectedMainCategory({ mainCategoryId: category.mainCategoryId, mainCategory: category.mainCategory, description: category.description });
    setMainCategory(category.mainCategory);
    setDescription(category.description);
    setShowEditMainCategoryModal(true);
  };

  const handleShowDeleteModal = (mainCategoryId, mainCategoryName) => {
    setSelectedMainCategory({ mainCategoryId, mainCategory: mainCategoryName });
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    setMainCategories((prevCategories) =>
      prevCategories.filter((category) => category.mainCategoryId !== selectedMainCategory.mainCategoryId)
    );
    setSnackbarMessage(`${selectedMainCategory.mainCategory} deleted successfully!`);
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    setShowDeleteModal(false);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (mainCategory && description) {
      const newMainCategory = {
        mainCategoryId: mainCategories.length + 1,
        mainCategory,
        description,
      };
      setMainCategories([...mainCategories, newMainCategory]);
      setShowAddMainCategoryModal(false);
       setSnackbarMessage('Saved successfully!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("success");
      setMainCategory('');
      setDescription('');
    } else {
      setSnackbarMessage('Please fill all fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleUpdate = () => {
    if (mainCategory && description) {
      const updatedMainCategories = mainCategories.map((category) =>
        category.mainCategoryId === selectedMainCategory.mainCategoryId
          ? { ...category, mainCategory, description }
          : category
      );
      setMainCategories(updatedMainCategories);
      setShowEditMainCategoryModal(false);
       setSnackbarMessage('Updated successfully!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("success");
      setMainCategory('');
      setDescription('');
    } else {
      setSnackbarMessage('Please fill all fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleReset = () => {
    setMainCategory('');
    setDescription('');
  };

  const handleCancel = () => {
    setShowEditMainCategoryModal(false);
    setMainCategory('');
    setDescription('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    setFilteredData(
      mainCategories.filter(
        (category) =>
          category.mainCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
          category.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, mainCategories]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <TextField
                           variant="outlined"
                           placeholder="Search..."
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           InputProps={{
                             startAdornment: (
                               <InputAdornment position="start">
                                 <SearchIcon
                                   sx={{
                                     color: theme.palette.primary.main,
                                     marginLeft: 1.5,
                                     fontSize: 24,
                                   }}
                                 />
                               </InputAdornment>
                             ),
                             endAdornment: searchTerm && (
                               <InputAdornment position="end">
                                 <IconButton
                                   size="small"
                                   onClick={() => setSearchTerm('')}
                                   sx={{
                                     color: "grey",
                                     marginRight: 1,
                                   }}
                                 >
                                   <CloseIcon fontSize="small" />
                                 </IconButton>
                               </InputAdornment>
                             ),
                             sx: {
                               borderRadius: '30px',
                               backgroundColor: theme.palette.mode === 'dark'
                                 ? 'rgba(255, 255, 255, 0.15)'
                                 : 'rgba(255, 255, 255, 1)',
                               boxShadow: theme.palette.mode === 'dark'
                                 ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                 : '0 2px 8px rgba(0, 0, 0, 0.1)',
                               '& .MuiOutlinedInput-notchedOutline': {
                                 border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                               },
                               '&:hover': {
                                 backgroundColor: theme.palette.mode === 'dark'
                                   ? 'rgba(255, 255, 255, 0.2)'
                                   : 'rgba(240, 240, 240, 1)',
                                 boxShadow: theme.palette.mode === 'dark'
                                   ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                                   : '0 2px 12px rgba(0, 0, 0, 0.15)',
                               },
                               '&.Mui-focused': {
                                 backgroundColor: theme.palette.mode === 'dark'
                                   ? 'rgba(255, 255, 255, 0.25)'
                                   : 'rgba(255, 255, 255, 1)',
                                 boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                                 '& .MuiOutlinedInput-notchedOutline': {
                                   border: `2px solid ${theme.palette.primary.main}`,
                                 },
                               },
                               paddingLeft: 1.5,
                               height: '50px',
                               transition: 'all 0.3s ease',
                             },
                           }}
                           sx={{
                             width: 350,
                             maxWidth: '100%',
                             '& .MuiInputBase-input': {
                               padding: '10px 14px',
                               color: theme.palette.text.primary,
                               fontWeight: 500,
                             },
                           }}
                         />
        <Box sx={{display :'flex',justifyContent:'center',alignItems:'center',gap:2}}>
          <Button
            variant="contained"
            onClick={handleAddMainCategory}
            sx={{
              ml: 2,
              width: '100px',
              borderRadius:'10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD
          </Button>
          </Box>
          <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0, // Remove MUI's default padding
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>
<Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Main Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Description</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((category, index) => (
                    <TableRow
                      key={category.mainCategoryId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{category.mainCategory}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{category.description}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(category)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleShowDeleteModal(category.mainCategoryId, category.mainCategory)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}>
                    <TableCell colSpan={4} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Search Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedMainCategory.mainCategory}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Main Category Modal */}
      <Modal
        open={showAddMainCategoryModal}
        onClose={() => setShowAddMainCategoryModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Main Category
            </Typography>
            <IconButton onClick={() => setShowAddMainCategoryModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <TextField
              label="Main Category"
              variant="outlined"
              fullWidth
              value={mainCategory}
              onChange={(e) => setMainCategory(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!mainCategory || !description ? "Enter Main Category and Description" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!mainCategory || !description}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit Main Category Modal */}
      <Modal
        open={showEditMainCategoryModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit Main Category
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <TextField
              label="Main Category"
              variant="outlined"
              fullWidth
              value={mainCategory}
              onChange={(e) => setMainCategory(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!mainCategory || !description ? "Enter Main Category and Description" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleUpdate}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!mainCategory || !description}
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {showAddMainCategoryModal ? 'Main Category Added ✅' : 'Main Category Updated ✅'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              {showAddMainCategoryModal ? 'Main Category Added Successfully' : 'Main Category Updated Successfully'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal
  open={showUserGuideModal}
  onClose={() => setShowUserGuideModal(false)}
  BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 800,
      bgcolor: 'background.paper',
      borderRadius: '16px',
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
        Main Category Management - User Guide
      </Typography>
      <IconButton
        onClick={() => setShowUserGuideModal(false)}
        sx={{ color: '#fff', position: 'absolute', right: 14 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Scrollable Content */}
   <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Add Main Category */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to add a new main category:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Click the <strong>"ADD"</strong> button at the top-right corner to open the Add Main Category modal.
    </li>
    <li>
      Enter the <strong>Main Category</strong> name in the provided text field.
    </li>
    <li>
      Enter a <strong>Description</strong> in the multiline text field.
    </li>
    <li>
      Before clicking the <strong>"SAVE"</strong> button, carefully review the entered details for accuracy.
    </li>
    <li>
      Click the <strong>"SAVE"</strong> button to add the main category. A success message will confirm the addition.
    </li>
    <li>
      The newly added main category will then appear in the <strong>Main Category List</strong> table.
    </li>
    <li>
      To clear the form and close the modal, click the <strong>"RESET"</strong> button.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Main Category and Description are mandatory fields.
  </Typography>

  {/* Section 2: Edit Main Category */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to update an existing main category:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the main category record you want to edit or update from the <strong>Main Category List</strong> table.
    </li>
    <li>
      Click the <strong>Edit</strong> icon (pencil) in the Action column to open the Edit Main Category modal.
    </li>
    <li>
      Update the <strong>Main Category</strong> name or <strong>Description</strong> as required.
    </li>
    <li>
      Before clicking the <strong>"UPDATE"</strong> button, ensure all required fields are properly filled.
    </li>
    <li>
      Click the <strong>"UPDATE"</strong> button to save the changes. A success message will confirm the update.
    </li>
    <li>
      To discard changes and close the modal, click the <strong>"CANCEL"</strong> button.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Main Category and Description are mandatory fields.
  </Typography>

  {/* Section 3: Delete Main Category */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete a main category:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the main category record you want to delete from the <strong>Main Category List</strong> table.
    </li>
    <li>
      Click the <strong>Delete</strong> icon (trash) in the Action column.
    </li>
    <li>
      Confirm the deletion by clicking <strong>"Yes, Delete"</strong> in the confirmation modal.
    </li>
    <li>
      The main category will be removed from the list, and a success message will be displayed.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted main categories cannot be recovered. Please proceed with caution.
  </Typography>

  {/* Section 4: Search Main Categories */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to search for a main category:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the search bar located at the top-left of the <strong>Main Category List</strong>.
    </li>
    <li>
      Enter the <strong>Main Category</strong> name or <strong>Description</strong> (or part of either) to filter the results.
    </li>
    <li>
      The table will automatically update to display matching main categories.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The search is case-insensitive and filters by Main Category and Description.
  </Typography>

  {/* Section 5: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the main category list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the pagination controls at the bottom of the table to browse through main category records.
    </li>
    <li>
      Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to move between pages.
    </li>
    <li>
      Click on a specific page number to navigate directly to that page.
    </li>
    <li>
      Adjust the number of rows displayed per page using the dropdown menu (for example, 5, 10, 25, or 50 rows).
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>
</CustomTableScrollbar>

  </Box>
</Modal>
<Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </Container>
  );
};

export default MainCategory;